import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getSiteContent } from '../lib/api';
import { isPublicShopEnabled } from '../lib/config';
import OrderPage from '../components/OrderPage';
import OpeningHours from '../components/OpeningHours';
import SmokeFooter from '../components/SmokeFooter';
import SmokeNav from '../components/SmokeNav';
import { trackUmami } from '../lib/umami';
import { formatProductPrice } from '../lib/priceFormat';
import { getCmsUrlValue, getCmsValue, normalizeSiteContent } from '../lib/siteContent';
import { buildPublicStructuredData, PAGE_SEO, useJsonLd, useSEO } from '../lib/seo';
import './SmokehouseSite.css';

const A = '/assets/smokehouse/';
const SMOKEHOUSE_LOGO = `${A}harryda-bbq-logo-400.webp`;

const shopProducts = [
  { name: 'Härryda BBQ Keps', type: 'Keps', text: 'Klassisk keps med broderad BBQ-känsla.', price: 299, variants: ['Svart', 'Camo'], visual: 'cap' },
  { name: 'Härryda BBQ T-shirt', type: 'T-shirt', text: 'Mjuk t-shirt med tryck fram och avslappnad passform.', price: 349, variants: ['S', 'M', 'L', 'XL', 'XXL', 'Svart'], visual: 'tee' },
  { name: 'Härryda BBQ Väska', type: 'Väska', text: 'Tygväska för rub, sås, handdukar och vardagsgrejer.', price: 249, variants: ['Natur', 'Svart'], visual: 'tote' },
];

const SHOP_ORDER_INITIAL = {
  name: '',
  email: '',
  phone: '',
  pickup_date: '',
  notes: '',
};

const CONTACT_FORM_INITIAL = {
  name: '',
  email: '',
  phone: '',
  subject: '',
  message: '',
};

const CONTACT_SUBJECTS = [
  { value: 'catering', label: 'Catering' },
  { value: 'produkter', label: 'Produkter' },
  { value: 'samarbete', label: 'Samarbete' },
  { value: 'annat', label: 'Annat' },
];

const homeValueSections = ['home_value_1', 'home_value_2', 'home_value_3'];
const premiumProofFields = ['proof_1', 'proof_2', 'proof_3', 'proof_4'];
const MIN_NAME_LENGTH = 2;
const MIN_PHONE_LENGTH = 6;
const MIN_MESSAGE_LENGTH = 10;

function money(value) {
  return new Intl.NumberFormat('sv-SE').format(value) + ' kr';
}

function inferProductVisual(product) {
  const name = `${product.name || ''} ${product.category || ''}`.toLowerCase();

  if (name.includes('leverpastej')) return 'pate';
  if (name.includes('köttbull') || name.includes('kottbull')) return 'meatballs';
  if (name.includes('bacon')) return 'bacon';
  if (name.includes('skinka') || name.includes('julskinka')) return 'ham';
  if (name.includes('korv')) return 'sausage';

  return 'food';
}

function normalizeShopVariant(variant, index) {
  const attributes = variant.attributes && typeof variant.attributes === 'object'
    ? Object.fromEntries(Object.entries(variant.attributes).filter(([, value]) => Boolean(value)))
    : {};
  const attributeValues = Object.values(attributes);
  const title = variant.title || attributeValues.join(' / ') || variant.sku || `Variant ${index + 1}`;

  return {
    id: variant.id,
    attributes,
    title,
    label: title,
    price: Number.parseFloat(variant.price ?? 0) || 0,
    image: variant.image_url || null,
  };
}

function normalizeShopProduct(product, index, source = 'backend') {
  const images = Array.isArray(product.image_gallery) ? product.image_gallery : [];
  const image = product.image_url || images[0] || null;
  const variants = (product.variants || [])
    .map((variant, variantIndex) => normalizeShopVariant(variant, variantIndex))
    .filter((variant) => variant.id && variant.price > 0);
  const defaultVariant = variants[0] || null;
  const price = defaultVariant?.price || Number.parseFloat(product.price_per_kg ?? product.price ?? 0) || 0;
  const visual = product.visual || (image ? null : inferProductVisual(product));

  return {
    id: `${source}-${product.id || product.name || index}`,
    productId: source === 'backend' ? product.id : null,
    source,
    name: product.name,
    type: product.category || product.type || 'Produkt',
    text: product.short_description || product.description || product.text || 'Rökigt hantverk från Härryda BBQ.',
    price,
    unitLabel: 'st',
    priceLabel: money(price),
    variants,
    defaultVariantId: defaultVariant?.id || null,
    image: image || `${A}logo-patch.jpg`,
    visual,
    available: product.is_active !== false && variants.length > 0,
  };
}

function buildShopProducts(backendProducts) {
  const merchProducts = shopProducts.map((product, index) => normalizeShopProduct(product, index, 'merch'));
  return backendProducts.length > 0 ? backendProducts : merchProducts;
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

async function getSmokehouseShopProducts() {
  const response = await fetch('/api/shop/products');
  if (!response.ok) throw new Error('Kunde inte hämta shopprodukter');
  return response.json();
}

async function submitSmokehouseShopOrder(orderData) {
  const response = await fetch('/api/shop/orders', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(orderData),
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(data?.detail || 'Beställningen kunde inte skickas.');
  }
  return data;
}

async function submitContactRequest(payload) {
  const response = await fetch('/api/contact', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    if (response.status === 429) throw new Error('För många meddelanden på kort tid. Försök igen om en stund.');
    throw new Error(data?.detail || 'Meddelandet kunde inte skickas.');
  }
  return data;
}

const ATTRIBUTE_LABELS = {
  size: 'Storlek',
  color: 'Färg',
};

const ATTRIBUTE_ORDER = ['size', 'color'];

const scrollToTopAfterNavigation = () => {
  window.setTimeout(() => window.scrollTo({ top: 0, left: 0, behavior: 'auto' }), 0);
};

function getVariantGroups(product) {
  const keys = Array.from(new Set(
    product.variants.flatMap((variant) => Object.keys(variant.attributes || {}))
  )).sort((a, b) => {
    const aIndex = ATTRIBUTE_ORDER.indexOf(a);
    const bIndex = ATTRIBUTE_ORDER.indexOf(b);
    if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;
    return aIndex - bIndex;
  });

  return keys.map((key) => ({
    key,
    label: ATTRIBUTE_LABELS[key] || key,
    values: Array.from(new Set(
      product.variants.map((variant) => variant.attributes?.[key]).filter(Boolean)
    )),
  })).filter((group) => group.values.length > 0);
}

export function Shell({ children, siteContent = {}, className = '', style }) {
  return (
    <div className={`smokehouse ${className}`.trim()} style={style}>
      <SmokeNav siteContent={siteContent} />
      <main>{children}</main>
      <SmokeFooter siteContent={siteContent} />
    </div>
  );
}

function Home({ siteContent = {} }) {
  const cms = (section, field) => getCmsValue(siteContent, section, field);
  const cmsUrl = (section, field) => getCmsUrlValue(siteContent, section, field);
  const pillars = homeValueSections.map((section) => ({
    section,
    kicker: cms(section, 'kicker'),
    title: cms(section, 'title'),
    body: cms(section, 'body'),
  }));
  const proofLines = premiumProofFields.map((field) => cms('home_premium_proof', field)).filter(Boolean);
  const meatLinks = [1, 2]
    .map((index) => ({
      label: cms('home_premium_proof', `link_${index}_label`),
      url: cmsUrl('home_premium_proof', `link_${index}_url`),
    }))
    .filter(({ label, url }) => label && url);

  return (
    <Shell siteContent={siteContent}>
      <section className="smoke-section smoke-hero">
        <div className="smoke-hero-bg" />
        <div className="smoke-wrap smoke-hero-content">
          <div className="smoke-hero-copy">
            <img className="smoke-hero-logo" src={SMOKEHOUSE_LOGO} alt={cms('global_nav', 'brand_alt')} />
            <span className="smoke-kicker">{cms('home_hero', 'kicker')}</span>
            <h1>{cms('home_hero', 'title')}</h1>
            <p className="smoke-lead">{cms('home_hero', 'lead')}</p>
            <div className="smoke-actions">
              <Link className="smoke-btn" to={cmsUrl('home_hero', 'primary_cta_url')} onClick={scrollToTopAfterNavigation}>{cms('home_hero', 'primary_cta_label')}</Link>
            </div>
          </div>
          <div className="smoke-hero-rail">
            <div className="smoke-hero-card" aria-label={cms('home_hero', 'card_aria_label')}>
              <span>{cms('home_hero', 'card_eyebrow')}</span>
              <strong>{cms('home_hero', 'card_title')}</strong>
              <p>{cms('home_hero', 'card_body')}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="smoke-routes" id="varderingar">
        <div className="smoke-wrap">
          <span className="smoke-kicker">{cms('home_values_header', 'kicker')}</span>
          <h2>{cms('home_values_header', 'heading')}</h2>
          <p className="smoke-lead">{cms('home_values_header', 'lead')}</p>
          <div className="smoke-route-grid">
            {pillars.map((pillar) => (
              <article className="smoke-route" key={pillar.section}>
                <span className="smoke-kicker">{pillar.kicker}</span>
                <div>
                  <h3>{pillar.title}</h3>
                  <p>{pillar.body}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="smoke-proof">
        <div className="smoke-wrap smoke-split">
          <div>
            <span className="smoke-kicker">{cms('home_premium_proof', 'kicker')}</span>
            <h2>{cms('home_premium_proof', 'heading')}</h2>
            <p className="smoke-lead">{cms('home_premium_proof', 'lead')}</p>
            <div className="smoke-proof-list">
              {proofLines.map((line) => <span key={line}>{line}</span>)}
            </div>
            {meatLinks.length > 0 && (
              <div className="smoke-proof-links">
                {cms('home_premium_proof', 'links_title') && <p>{cms('home_premium_proof', 'links_title')}</p>}
                <div className="smoke-actions">
                  {meatLinks.map(({ label, url }) => (
                    <Link className="smoke-btn smoke-btn-secondary" key={url} to={url} onClick={scrollToTopAfterNavigation}>{label}</Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="smoke-panel">
            <span className="smoke-kicker">{cms('home_next_step', 'kicker')}</span>
            <h3>{cms('home_next_step', 'title')}</h3>
            <p>{cms('home_next_step', 'body')}</p>
            <Link className="smoke-btn" to={cmsUrl('home_next_step', 'cta_url')} onClick={scrollToTopAfterNavigation}>{cms('home_next_step', 'cta_label')}</Link>
          </div>
        </div>
      </section>
    </Shell>
  );
}

function Shop({ siteContent = {} }) {
  const [products, setProducts] = useState([]);
  const [selectedVariants, setSelectedVariants] = useState({});
  const [cart, setCart] = useState([]);
  const [orderForm, setOrderForm] = useState(SHOP_ORDER_INITIAL);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [orderStatus, setOrderStatus] = useState({ type: '', message: '' });
  const [usingFallbackProducts, setUsingFallbackProducts] = useState(false);
  const sum = cart.reduce((total, item) => total + item.unitPrice * item.quantity, 0);
  const backendReady = !usingFallbackProducts && products.some((product) => product.productId);

  useEffect(() => {
    let ignore = false;

    const loadShop = async () => {
      setLoading(true);
      const productData = await getSmokehouseShopProducts();

      if (ignore) return;

      const backendProducts = (productData || [])
        .map((product, index) => normalizeShopProduct(product, index, 'backend'))
        .filter((product) => product.name && product.price > 0);
      const nextProducts = buildShopProducts(backendProducts);

      if (backendProducts.length > 0) {
        setProducts(nextProducts);
        setSelectedVariants(Object.fromEntries(
          nextProducts.map((product) => [product.id, product.defaultVariantId]).filter(([, variantId]) => variantId)
        ));
        setUsingFallbackProducts(false);
        setOrderStatus({ type: '', message: '' });
      } else {
        setProducts(nextProducts);
        setUsingFallbackProducts(true);
        setOrderStatus({
          type: 'error',
          message: 'Shoppen visas, men admin har inga orderbara shopprodukter ännu.',
        });
      }

      setLoading(false);
    };

    loadShop();
    return () => {
      ignore = true;
    };
  }, []);

  const addToCart = (product) => {
    const variantId = selectedVariants[product.id] || product.defaultVariantId;
    const variant = product.variants.find((item) => item.id === variantId);

    if (!product.productId || !variant) {
      setOrderStatus({
        type: 'error',
        message: 'Produkten saknar orderbar variant i admin-Shop.',
      });
      return;
    }

    setCart((current) => {
      const existing = current.find((item) => item.variantId === variant.id);
      if (existing) {
        return current.map((item) => (
          item.variantId === variant.id ? { ...item, quantity: item.quantity + 1 } : item
        ));
      }
      return [
        ...current,
        {
          productId: product.productId,
          variantId: variant.id,
          name: product.name,
          type: product.type,
          variantTitle: variant.label,
          unitLabel: product.unitLabel,
          quantity: 1,
          unitPrice: variant.price,
        },
      ];
    });
    setOrderStatus({ type: '', message: '' });
  };

  const updateCartQuantity = (variantId, delta) => {
    setCart((current) => current
      .map((item) => (
        item.variantId === variantId ? { ...item, quantity: Math.max(0, item.quantity + delta) } : item
      ))
      .filter((item) => item.quantity > 0));
  };

  const handleOrderChange = (event) => {
    const { name, value } = event.target;
    setOrderForm((current) => ({ ...current, [name]: value }));
  };

  const handleOrderSubmit = async (event) => {
    event.preventDefault();

    if (!backendReady) {
      setOrderStatus({ type: 'error', message: 'Backend behöver returnera riktiga produkter innan order kan skickas.' });
      return;
    }
    if (cart.length === 0) {
      setOrderStatus({ type: 'error', message: 'Lägg till minst en produkt i varukorgen.' });
      return;
    }
    if (orderForm.name.trim().length < MIN_NAME_LENGTH) {
      setOrderStatus({ type: 'error', message: 'Ange namn.' });
      return;
    }
    if (!isValidEmail(orderForm.email)) {
      setOrderStatus({ type: 'error', message: 'Ange en giltig e-postadress.' });
      return;
    }
    if (orderForm.phone.trim().length < MIN_PHONE_LENGTH) {
      setOrderStatus({ type: 'error', message: 'Ange telefonnummer.' });
      return;
    }
    setSubmitting(true);
    setOrderStatus({ type: '', message: '' });

    try {
      const result = await submitSmokehouseShopOrder({
        customer: {
          name: orderForm.name,
          email: orderForm.email,
          phone: orderForm.phone,
        },
        delivery_method: 'pickup',
        notes: [
          orderForm.pickup_date ? `Önskat upphämtningsdatum: ${orderForm.pickup_date}` : '',
          orderForm.notes || '',
        ].filter(Boolean).join('\n\n') || null,
        items: cart.map((item) => ({
          variant_id: item.variantId,
          quantity: item.quantity,
        })),
      });

      setCart([]);
      setOrderForm(SHOP_ORDER_INITIAL);
      setOrderStatus({
        type: 'success',
        message: result.data?.order_number
          ? `Beställning mottagen: ${result.data.order_number}. Vi återkommer med bekräftelse.`
          : 'Beställning mottagen. Vi återkommer med bekräftelse.',
      });
    } catch (error) {
      setOrderStatus({ type: 'error', message: error.message || 'Beställningen kunde inte skickas.' });
    } finally {
      setSubmitting(false);
    }
  };

  const setProductVariant = (productId, variantId) => {
    setSelectedVariants((current) => ({
      ...current,
      [productId]: variantId,
    }));
  };

  const selectedVariantFor = (product) => {
    const variantId = selectedVariants[product.id] || product.defaultVariantId;
    return product.variants.find((variant) => variant.id === variantId) || product.variants[0] || null;
  };

  const setProductOption = (product, key, value) => {
    const selectedVariant = selectedVariantFor(product);
    const nextAttributes = {
      ...(selectedVariant?.attributes || {}),
      [key]: value,
    };
    const matchingVariant = product.variants.find((variant) => (
      Object.entries(nextAttributes).every(([attrKey, attrValue]) => variant.attributes?.[attrKey] === attrValue)
    )) || product.variants.find((variant) => variant.attributes?.[key] === value);

    if (matchingVariant) setProductVariant(product.id, matchingVariant.id);
  };

  return (
    <Shell siteContent={siteContent}>
      <section className="smoke-page">
        <div className="smoke-wrap">
          <div className="smoke-page-head">
            <div><span className="smoke-kicker">Shop</span><h1>Beställ från rökverket.</h1><p className="smoke-lead">Produkter från befintlig backend, varukorg och orderflöde på samma premiumyta. Vi bekräftar upphämtning innan allt är spikat.</p></div>
            <img src={`${A}logo-patch.jpg`} alt="Härryda BBQ patch" />
          </div>
          {orderStatus.message && <p className={`smoke-form-status smoke-shop-status ${orderStatus.type}`} aria-live="polite">{orderStatus.message}</p>}
          <div className="smoke-shop-layout">
            <div className="smoke-products">
              {loading ? (
                <div className="smoke-product smoke-shop-empty" aria-busy="true">
                  <div>
                    <span className="smoke-kicker">Laddar</span>
                    <h3>Hämtar produkter...</h3>
                    <p>Shoppen synkar mot backend.</p>
                  </div>
                </div>
              ) : products.map((product) => {
                const selectedVariant = selectedVariantFor(product);
                const variantGroups = getVariantGroups(product);
                return (
                  <article className="smoke-product" key={product.id}>
                    <div className="smoke-product-image">
                      {product.visual ? (
                        <div className={`smoke-product-art smoke-product-art-${product.visual}`} aria-hidden="true">
                          <img src={SMOKEHOUSE_LOGO} alt="" aria-hidden="true" />
                        </div>
                      ) : (
                        <img src={selectedVariant?.image || product.image} alt={product.name} />
                      )}
                    </div>
                    <div>
                      <span className="smoke-kicker">{product.type}</span>
                      <h3>{product.name}</h3>
                      <p>{product.text}</p>
                      {variantGroups.length > 0 ? (
                        <div className="smoke-variant-groups" aria-label={`Varianter för ${product.name}`}>
                          {variantGroups.map((group) => (
                            <div className="smoke-variant-group" key={group.key}>
                              <span>{group.label}</span>
                              <div className="smoke-variants">
                                {group.values.map((value) => (
                                  <button
                                    key={`${group.key}-${value}`}
                                    type="button"
                                    className={selectedVariant?.attributes?.[group.key] === value ? 'active' : ''}
                                    onClick={() => setProductOption(product, group.key, value)}
                                  >
                                    {value}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : product.variants.length > 0 && (
                        <div className="smoke-variants" aria-label={`Varianter för ${product.name}`}>
                          {product.variants.map((variant) => (
                            <button
                              key={variant.id}
                              type="button"
                              className={selectedVariant?.id === variant.id ? 'active' : ''}
                              onClick={() => setProductVariant(product.id, variant.id)}
                            >
                              {variant.label}
                            </button>
                          ))}
                        </div>
                      )}
                      <p className="smoke-product-price"><strong>{selectedVariant ? money(selectedVariant.price) : product.priceLabel}</strong></p>
                      <button className="smoke-btn" type="button" disabled={!product.available || !product.productId} onClick={() => addToCart(product)}>
                        {product.productId ? 'Lägg i varukorg' : 'Visas - ej i admin'}
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
            <aside className="smoke-cart">
              <form onSubmit={handleOrderSubmit}>
                <span className="smoke-kicker">Varukorg</span>
                <h3>{cart.reduce((total, item) => total + item.quantity, 0)} st</h3>
                {cart.length === 0 ? (
                  <p>Varukorgen är tom.</p>
                ) : cart.map((item) => (
                  <div className="smoke-cart-item" key={item.variantId}>
                    <div>
                      <strong>{item.name}</strong>
                      <span>{item.variantTitle} · {money(item.unitPrice)} / {item.unitLabel}</span>
                    </div>
                    <div className="smoke-cart-qty">
                      <button type="button" aria-label={`Minska ${item.name}`} onClick={() => updateCartQuantity(item.variantId, -1)}>-</button>
                      <span>{item.quantity}</span>
                      <button type="button" aria-label={`Öka ${item.name}`} onClick={() => updateCartQuantity(item.variantId, 1)}>+</button>
                    </div>
                  </div>
                ))}
                <div className="smoke-cart-line"><span>Summa</span><span>{money(sum)}</span></div>
                <div className="smoke-cart-line"><span>Orderstatus</span><span>{backendReady ? 'Backend kopplad' : 'Fallbackläge'}</span></div>
                <label>Namn *<input name="name" value={orderForm.name} onChange={handleOrderChange} autoComplete="name" /></label>
                <label>E-post *<input type="email" name="email" value={orderForm.email} onChange={handleOrderChange} autoComplete="email" /></label>
                <label>Telefon *<input type="tel" name="phone" value={orderForm.phone} onChange={handleOrderChange} autoComplete="tel" /></label>
                <label>Önskat upphämtningsdatum
                  <input type="date" name="pickup_date" value={orderForm.pickup_date} onChange={handleOrderChange} />
                </label>
                <label>Meddelande<textarea name="notes" value={orderForm.notes} onChange={handleOrderChange} placeholder="Skriv om du önskar leverans, särskild tid eller annan detalj." /></label>
                <button className="smoke-btn" type="submit" disabled={submitting || cart.length === 0 || !backendReady}>{submitting ? 'Skickar...' : 'Skicka beställning'}</button>
                <p>Ordern går till befintlig backend och bekräftas manuellt innan upphämtning.</p>
              </form>
            </aside>
          </div>
        </div>
      </section>
    </Shell>
  );
}

const PRODUCT_PREVIEW_GROUPS = [
  {
    id: 'bbq',
    label: 'BBQ från röken',
    eyebrow: 'Långrökt',
    title: 'Det som gör gäster tysta en stund.',
    text: 'Proteiner, sides och såser som byggs runt rök, tid och råvara. För catering, batcher och större beställningar.',
  },
  {
    id: 'chark',
    label: 'Chark från smokehouse',
    eyebrow: 'Chark',
    title: 'Rökta småskaliga produkter med egen karaktär.',
    text: 'Bacon, pastej, korv, skinka och annat som passar i butik, på julbordet eller i kylen hemma.',
  },
];

function productPreviewGroup(product) {
  const explicitCategory = String(product.category || product.group || '').trim().toLowerCase();
  if (explicitCategory === 'bbq' || explicitCategory === 'bbq från röken') return 'bbq';
  if (explicitCategory === 'chark' || explicitCategory === 'chark från smokehouse') return 'chark';

  const source = `${product.group || ''} ${product.category || ''} ${product.name || ''}`.toLowerCase();
  if (source.includes('chark') || source.includes('bacon') || source.includes('leverpastej') || source.includes('köttbull') || source.includes('kottbull') || source.includes('korv') || source.includes('skinka')) return 'chark';
  return 'bbq';
}

function productPreviewPrice(product, fallbackLabel = 'Pris på förfrågan') {
  const price = Number.parseFloat(product.price);
  if (!Number.isFinite(price) || price <= 0) return product.priceText || fallbackLabel;
  const unit = typeof product.priceUnit === 'string' ? product.priceUnit.trim() : '';
  if (unit) return `${money(price)} / ${unit}`;
  const displayUnit = product.metadata?.price_display_unit;
  if (displayUnit === 'hg' || displayUnit === 'kg' || displayUnit === 'st') {
    const formatted = formatProductPrice(price, displayUnit);
    return formatted || money(price);
  }
  return money(price);
}

function buildProductTags(product) {
  const tags = [];
  if (product.category) tags.push(product.category);
  if (product.weight_grams) tags.push(`${product.weight_grams} g`);
  return [...new Set(tags.filter(Boolean))];
}

function normalizeProductText(value) {
  return String(value || '').trim();
}

function isSameProductText(first, second) {
  return normalizeProductText(first).toLocaleLowerCase('sv-SE') === normalizeProductText(second).toLocaleLowerCase('sv-SE');
}

function mapDatabaseProduct(product) {
  const headline = normalizeProductText(product.short_description);
  const description = normalizeProductText(product.description || product.long_description);

  return {
    slug: product.slug.trim(),
    group: productPreviewGroup(product),
    name: product.name.trim(),
    headline,
    description: isSameProductText(headline, description) ? '' : description,
    tags: buildProductTags(product),
    serve: normalizeProductText(product.pairing_suggestions || product.cooking_instructions),
    visual: product.visual || inferProductVisual(product),
    image_url: product.image_url,
    price: product.price,
    priceUnit: product.price_unit,
    metadata: product.metadata,
    sort_order: Number.isFinite(Number(product.sort_order)) ? Number(product.sort_order) : 0,
  };
}

function getProductsForDisplay(apiProducts = []) {
  return (apiProducts || [])
    .filter((product) => (
      product?.is_active === true
      && typeof product.name === 'string'
      && product.name.trim()
      && typeof product.slug === 'string'
      && product.slug.trim()
    ))
    .map(mapDatabaseProduct)
    .sort((left, right) => (left.sort_order - right.sort_order) || left.name.localeCompare(right.name, 'sv'));
}

function ProductPreviewIllustration({ product }) {
  if (product.image_url) {
    return <img src={product.image_url} alt="" aria-hidden="true" loading="lazy" />;
  }
  return (
    <div className={`smoke-product-art smoke-product-art-${product.visual || 'food'}`} aria-hidden="true">
      <strong>{product.name.slice(0, 1)}</strong>
      <span>{product.visual === 'bacon' ? 'Rökt i huset' : 'Från röken'}</span>
    </div>
  );
}

function ProductsV2({ siteContent = {} }) {
  const [activeGroup, setActiveGroup] = useState('bbq');
  const [apiProducts, setApiProducts] = useState([]);
  const [loadState, setLoadState] = useState('loading');
  const [productStickyTop, setProductStickyTop] = useState(90);
  const productSectionRef = useRef(null);
  const productTabsRef = useRef(null);
  const userSelectedGroupRef = useRef(false);
  const cms = (section, field) => getCmsValue(siteContent, section, field);
  const cmsUrl = (section, field) => getCmsUrlValue(siteContent, section, field);

  const productGroups = useMemo(() => PRODUCT_PREVIEW_GROUPS.map((group) => {
    const section = `products_v2_group_${group.id}`;
    return {
      ...group,
      eyebrow: getCmsValue(siteContent, section, 'eyebrow'),
      label: getCmsValue(siteContent, section, 'label'),
      title: getCmsValue(siteContent, section, 'title'),
      text: getCmsValue(siteContent, section, 'text'),
      linkLabel: getCmsValue(siteContent, section, 'link_label'),
      linkUrl: getCmsUrlValue(siteContent, section, 'link_url'),
    };
  }), [siteContent]);

  useEffect(() => {
    let ignore = false;
    const loadProducts = async () => {
      try {
        const response = await fetch('/api/products');
        if (!response.ok) throw new Error('Kunde inte hämta produkter');
        const data = await response.json();
        const rows = Array.isArray(data) ? data : [];
        if (!ignore) {
          setApiProducts(rows);
          setLoadState('ready');
        }
      } catch (error) {
        if (!ignore) {
          setApiProducts([]);
          setLoadState('error');
        }
      }
    };

    loadProducts();
    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    const updateProductStickyTop = () => {
      const nav = document.querySelector('.smoke-nav');
      const navHeight = nav?.getBoundingClientRect().height || 72;
      setProductStickyTop(Math.ceil(navHeight) + 18);
    };

    updateProductStickyTop();
    window.addEventListener('resize', updateProductStickyTop);
    return () => window.removeEventListener('resize', updateProductStickyTop);
  }, []);

  const products = useMemo(
    () => getProductsForDisplay(apiProducts),
    [apiProducts]
  );

  useEffect(() => {
    if (products.length === 0) return;
    if (userSelectedGroupRef.current) return;
    const activeHasProducts = products.some((product) => productPreviewGroup(product) === activeGroup);
    if (activeHasProducts) return;
    const nextGroup = productGroups.find((group) => products.some((product) => productPreviewGroup(product) === group.id));
    if (nextGroup) setActiveGroup(nextGroup.id);
  }, [activeGroup, productGroups, products]);

  const activeMeta = productGroups.find((group) => group.id === activeGroup) || productGroups[0];
  const visibleProducts = products.filter((product) => productPreviewGroup(product) === activeGroup);
  const hasProducts = products.length > 0;

  const selectGroup = (groupId) => {
    userSelectedGroupRef.current = true;
    setActiveGroup(groupId);
    window.requestAnimationFrame(() => {
      const target = productTabsRef.current || productSectionRef.current;
      if (!target) return;
      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
      window.scrollTo({
        top: Math.max(0, target.getBoundingClientRect().top + window.scrollY - productStickyTop),
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
    });
  };

  return (
    <Shell
      siteContent={siteContent}
      className="smokehouse-products-v2"
      style={{ '--smoke-products-sticky-top': `${productStickyTop}px` }}
    >
      <section className="smoke-products-v2-hero">
        <div className="smoke-products-v2-hero-bg" aria-hidden="true" />
        <div className="smoke-wrap smoke-products-v2-hero-inner">
          <div className="smoke-products-v2-hero-copy">
            <span className="smoke-kicker">{cms('products_v2_hero', 'kicker')}</span>
            <h1>{cms('products_v2_hero', 'title')}</h1>
            <p className="smoke-lead">{cms('products_v2_hero', 'lead')}</p>
            {hasProducts && <div className="smoke-products-v2-actions" aria-label={cms('products_v2_hero', 'category_aria_label')}>
              {productGroups.map((group) => (
                <button
                  key={group.id}
                  className={activeGroup === group.id ? 'active' : ''}
                  type="button"
                  aria-label={`${cms('products_v2_labels', 'show_prefix')} ${group.label}`}
                  aria-pressed={activeGroup === group.id}
                  onClick={() => selectGroup(group.id)}
                >
                  <span>{group.eyebrow}</span>
                  {group.label}
                </button>
              ))}
            </div>}
          </div>
        </div>
      </section>

      <section className="smoke-products-v2-section" ref={productSectionRef}>
        <div className="smoke-wrap">
          {hasProducts ? <>
            <div className="smoke-products-v2-section-head">
              <div>
                <span className="smoke-kicker">{activeMeta.eyebrow}</span>
                <h2>{activeMeta.title}</h2>
                <p>{activeMeta.text}</p>
                {activeMeta.linkLabel && activeMeta.linkUrl && (
                  <Link className="smoke-btn smoke-btn-secondary" to={activeMeta.linkUrl}>{activeMeta.linkLabel}</Link>
                )}
              </div>
              <Link className="smoke-btn smoke-btn-secondary" to={cmsUrl('products_v2_labels', 'contact_url')}>{cms('products_v2_labels', 'contact_label')}</Link>
            </div>

            <div className="smoke-products-v2-sticky-tabs" ref={productTabsRef} aria-label={cms('products_v2_labels', 'switch_aria_label')}>
              <span>{cms('products_v2_labels', 'sortiment_label')}</span>
              <div>
                {productGroups.map((group) => (
                  <button
                    key={group.id}
                    className={activeGroup === group.id ? 'active' : ''}
                    type="button"
                    aria-label={`${cms('products_v2_labels', 'switch_prefix')} ${group.label}`}
                    aria-pressed={activeGroup === group.id}
                    onClick={() => selectGroup(group.id)}
                  >
                    {group.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="smoke-products-v2-grid">
              {visibleProducts.map((product, index) => (
                <Link
                  className={`smoke-products-v2-card${index === 0 && visibleProducts.length > 1 ? ' featured' : ''}`}
                  key={product.slug}
                  to={`/produkter/${product.slug}`}
                >
                  <div className="smoke-products-v2-visual">
                    <ProductPreviewIllustration product={product} />
                  </div>
                  <div className="smoke-products-v2-card-copy">
                    <div className="smoke-products-v2-card-topline">
                      <span>{productPreviewPrice(product, cms('products_v2_labels', 'price_on_request'))}</span>
                    </div>
                    <h3>{product.name}</h3>
                    {product.headline && <p className="smoke-products-v2-headline">{product.headline}</p>}
                    {product.description && <p>{product.description}</p>}
                    <span className="smoke-products-v2-open">Se produkten</span>
                  </div>
                </Link>
              ))}
            </div>

            {visibleProducts.length === 0 && (
              <div className="smoke-products-v2-empty">
                <span>SORTIMENT</span>
                <h3>Inga aktiva produkter i den här kategorin just nu.</h3>
                <p>Kontakta oss för aktuell tillgänglighet och beställning.</p>
                <Link className="smoke-btn smoke-btn-secondary" to="/kontakt">Kontakta oss</Link>
              </div>
            )}
          </> : loadState !== 'loading' && (
            <div className="smoke-products-v2-empty">
              <span>SORTIMENT</span>
              <h3>Sortimentet uppdateras</h3>
              <p>Kontakta oss för aktuell tillgänglighet och beställning.</p>
              <Link className="smoke-btn smoke-btn-secondary" to="/kontakt">Kontakta oss</Link>
            </div>
          )}
        </div>
      </section>

      {hasProducts && <section className="smoke-products-v2-cta">
        <div className="smoke-wrap smoke-products-v2-cta-inner">
          <div>
            <span className="smoke-kicker">{cms('products_v2_cta', 'kicker')}</span>
            <h2>{cms('products_v2_cta', 'title')}</h2>
            <p>{cms('products_v2_cta', 'body')}</p>
          </div>
          <div className="smoke-products-v2-cta-actions">
            <Link className="smoke-btn" to={cmsUrl('products_v2_cta', 'primary_url')}>{cms('products_v2_cta', 'primary_label')}</Link>
            <Link className="smoke-btn smoke-btn-secondary" to={cmsUrl('products_v2_cta', 'secondary_url')}>{cms('products_v2_cta', 'secondary_label')}</Link>
          </div>
        </div>
      </section>}
    </Shell>
  );
}

function ContactForm({ siteContent = {} }) {
  const cms = (field) => getCmsValue(siteContent, 'contact_page', field);
  const [form, setForm] = useState(CONTACT_FORM_INITIAL);
  const [fieldErrors, setFieldErrors] = useState({});
  const [status, setStatus] = useState({ type: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const validate = () => {
    const errors = {};
    if (form.name.trim().length < MIN_NAME_LENGTH) errors.name = 'Ange ditt namn (minst 2 tecken).';
    if (!isValidEmail(form.email)) errors.email = 'Ange en giltig e-postadress.';
    if (!form.subject) errors.subject = 'Välj ett ämne.';
    if (form.message.trim().length < MIN_MESSAGE_LENGTH) errors.message = 'Skriv ett meddelande på minst 10 tecken.';
    return errors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setStatus({ type: 'error', message: 'Kontrollera fälten nedan och försök igen.' });
      return;
    }

    setSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      await submitContactRequest({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        subject: form.subject,
        message: form.message.trim(),
      });
      setForm(CONTACT_FORM_INITIAL);
      setStatus({ type: 'success', message: cms('form_success') });
      trackUmami('contact_sent');
    } catch (error) {
      setStatus({ type: 'error', message: error.message || 'Meddelandet kunde inte skickas.' });
    } finally {
      setSubmitting(false);
    }
  };

  const describedBy = (field) => (fieldErrors[field] ? `contact-${field}-error` : undefined);

  return (
    <section className="smoke-section smoke-contact-form-section" aria-labelledby="smoke-contact-form-title">
      <div className="smoke-wrap smoke-contact-form-layout">
        <div className="smoke-contact-form-intro">
          <span className="smoke-kicker">{cms('form_kicker')}</span>
          <h2 id="smoke-contact-form-title">{cms('form_title')}</h2>
          <p>{cms('form_body')}</p>
        </div>

        <form className="smoke-contact-form" onSubmit={handleSubmit} noValidate>
          {status.message && (
            <p className={`smoke-form-status ${status.type}`} role="alert" aria-live="polite">{status.message}</p>
          )}

          <div className="smoke-form-row">
            <label htmlFor="contact-name">
              Namn *
              <input
                id="contact-name"
                name="name"
                value={form.name}
                onChange={handleChange}
                autoComplete="name"
                aria-required="true"
                aria-invalid={fieldErrors.name ? 'true' : undefined}
                aria-describedby={describedBy('name')}
              />
              {fieldErrors.name && <small id="contact-name-error" className="smoke-field-error">{fieldErrors.name}</small>}
            </label>

            <label htmlFor="contact-email">
              E-post *
              <input
                id="contact-email"
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                autoComplete="email"
                aria-required="true"
                aria-invalid={fieldErrors.email ? 'true' : undefined}
                aria-describedby={describedBy('email')}
              />
              {fieldErrors.email && <small id="contact-email-error" className="smoke-field-error">{fieldErrors.email}</small>}
            </label>
          </div>

          <div className="smoke-form-row">
            <label htmlFor="contact-phone">
              Telefon (valfritt)
              <input
                id="contact-phone"
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                autoComplete="tel"
              />
            </label>

            <label htmlFor="contact-subject">
              Ämne *
              <select
                id="contact-subject"
                name="subject"
                value={form.subject}
                onChange={handleChange}
                aria-required="true"
                aria-invalid={fieldErrors.subject ? 'true' : undefined}
                aria-describedby={describedBy('subject')}
              >
                <option value="">Välj ämne</option>
                {CONTACT_SUBJECTS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              {fieldErrors.subject && <small id="contact-subject-error" className="smoke-field-error">{fieldErrors.subject}</small>}
            </label>
          </div>

          <label htmlFor="contact-message">
            Meddelande *
            <textarea
              id="contact-message"
              name="message"
              value={form.message}
              onChange={handleChange}
              placeholder="Berätta vad det gäller — datum, antal personer och plats om det gäller ett event."
              aria-required="true"
              aria-invalid={fieldErrors.message ? 'true' : undefined}
              aria-describedby={describedBy('message')}
            />
            {fieldErrors.message && <small id="contact-message-error" className="smoke-field-error">{fieldErrors.message}</small>}
          </label>

          <button className="smoke-btn" type="submit" disabled={submitting} aria-busy={submitting}>
            {submitting ? 'Skickar...' : cms('form_cta_label')}
          </button>
        </form>
      </div>
    </section>
  );
}

function ContactPage({ siteContent = {} }) {
  const cms = (field) => getCmsValue(siteContent, 'contact_page', field);
  const identity = (field) => getCmsValue(siteContent, 'business_identity', field);
  const phone = identity('phone');
  const email = identity('email');
  const phoneHref = phone ? `tel:${phone.replace(/[^\d+]/g, '')}` : '';
  const emailHref = email ? `mailto:${email.trim()}` : '';

  return (
    <Shell siteContent={siteContent}>
      <section className="smoke-section smoke-hero smoke-contact-hero">
        <div className="smoke-hero-bg" />
        <div className="smoke-wrap smoke-hero-content">
          <div className="smoke-hero-copy">
            <img className="smoke-hero-logo" src={SMOKEHOUSE_LOGO} alt={getCmsValue(siteContent, 'global_nav', 'brand_alt')} />
            <span className="smoke-kicker">{cms('kicker')}</span>
            <h1>{cms('title')}</h1>
            <p className="smoke-lead">{cms('lead')}</p>
            <div className="smoke-actions">
              {phone && <a className="smoke-btn" href={phoneHref}>{cms('primary_cta_label')}</a>}
              {email && <a className="smoke-btn smoke-btn-secondary" href={emailHref}>{cms('secondary_cta_label')}</a>}
            </div>
          </div>
          <div className="smoke-hero-rail">
            <div className="smoke-hero-card">
              <span>{cms('card_eyebrow')}</span>
              <strong>{cms('card_title')}</strong>
              <p>{cms('card_body')}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="smoke-routes smoke-contact-routes">
        <div className="smoke-wrap">
          <div className="smoke-route-grid">
            {phone && <div className="smoke-route">
              <span className="smoke-kicker">Telefon</span>
              <div>
                <h2>{phone}</h2>
                <p>{cms('phone_body')}</p>
                <a className="smoke-btn" href={phoneHref}>{cms('phone_cta_label')}</a>
              </div>
            </div>}
            {email && <div className="smoke-route">
              <span className="smoke-kicker">E-post</span>
              <div>
                <h2>{email}</h2>
                <p>{cms('email_body')}</p>
                <a className="smoke-btn smoke-btn-secondary" href={emailHref}>{cms('email_cta_label')}</a>
              </div>
            </div>}
            <div className="smoke-route">
              <span className="smoke-kicker">{cms('catering_label')}</span>
              <div>
                <h2>{cms('catering_title')}</h2>
                <p>{cms('catering_body')}</p>
                <Link className="smoke-btn smoke-btn-secondary" to="/catering" onClick={scrollToTopAfterNavigation}>{cms('catering_cta_label')}</Link>
              </div>
            </div>
            <OpeningHours className="smoke-route smoke-opening-hours-route" />
          </div>
        </div>
      </section>

      <ContactForm siteContent={siteContent} />
    </Shell>
  );
}

export default function SmokehouseSite() {
  const { pathname } = useLocation();
  const publicShopEnabled = isPublicShopEnabled();
  useSEO({ ...(PAGE_SEO[pathname] || PAGE_SEO['/']), path: pathname });
  const [siteContent, setSiteContent] = useState({});

  useEffect(() => {
    let ignore = false;

    const loadSiteContent = async () => {
      try {
        const content = await getSiteContent();
        const contentObject = normalizeSiteContent(content);
        if (!ignore && Object.keys(contentObject).length > 0) setSiteContent(contentObject);
      } catch (error) {
        if (!ignore) setSiteContent({});
      }
    };

    loadSiteContent();
    return () => {
      ignore = true;
    };
  }, []);

  const structuredData = useMemo(
    () => buildPublicStructuredData(siteContent, pathname),
    [pathname, siteContent]
  );
  useJsonLd('public-route-structured-data', structuredData);

  const page = useMemo(() => {
    if (pathname === '/produkter') return <ProductsV2 siteContent={siteContent} />;
    if (pathname === '/kontakt') return <ContactPage siteContent={siteContent} />;
    if (pathname === '/order') return <Shell siteContent={siteContent}><OrderPage siteContent={siteContent} /></Shell>;
    if (pathname === '/shop') return publicShopEnabled ? <Shop siteContent={siteContent} /> : <Home siteContent={siteContent} />;
    return <Home siteContent={siteContent} />;
  }, [pathname, publicShopEnabled, siteContent]);
  return page;
}
